<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { useElTable } from "~/core/composables/useElTable";
import { useToggle } from "~/core/composables/useToggle";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useFetchSectors } from "~/modules/sector/composables/useFetchSectors";
import {
  MAX_SECTOR_DEPTH,
  PATH_SECTORS_CODE,
} from "~/modules/sector/constants";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import { findSectorParentCode } from "~/modules/sector/utils/findSectorParentCode";
import { getGrantedSectors } from "~/modules/sector/utils/getGrantedSectors";
import { getSectorDepth } from "~/modules/sector/utils/getSectorDepth";

const { t } = useI18n();
const router = useRouter();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const { isLoading, error, sectors, getSectors } = useFetchSectors({
  organizationCode: currentOrganization.value?.code,
});
const [formDialogVisible, toggleFormDialogVisible] = useToggle();
const [removeDialogVisible, toggleRemoveDialogVisible] = useToggle();
const { getCellStyle } = useElTable();
const { can } = useAcl();

const searchQuery = ref<string>("");
const selectedSector = ref<Sector | undefined>();

const parentSectorCode = ref<number | undefined>();

const sectorsTree: Ref<Sector[]> = ref([]);

const grantedSectors = computed(() => getGrantedSectors(sectors.value));

/**
 * Filter the sector tree with the searchQuery value.
 * Execute a case-insensitive search.
 * @param sectors
 * @param search
 */
function getFilteredSectors(sectors: Sector[], search: string): Sector[] {
  const filteredSectors = [];

  for (const sector of sectors) {
    let filteredChildrenSectors: Sector[] = [];

    if (sector.children.length > 0) {
      filteredChildrenSectors = getFilteredSectors(sector.children, search);
    }

    if (
      search === "" ||
      sector.name.toLowerCase().includes(search.toLowerCase()) ||
      filteredChildrenSectors.length > 0
    ) {
      filteredSectors.push({ ...sector, children: filteredChildrenSectors });
    }
  }

  return filteredSectors;
}

async function onSearchSectors() {
  await getSectors(searchQuery.value);

  sectorsTree.value = getFilteredSectors(sectors.value, searchQuery.value);
}

/**
 * On select a specific sector
 * @param sector
 */
function onSelectSector(sector: Sector) {
  if (canUpdateSector(sector.code)) {
    router.push({ name: PATH_SECTORS_CODE, params: { code: sector.code } });
  }
}

function onCreateSector(sector: Sector) {
  setSelectedSector();
  parentSectorCode.value = sector?.code;
  toggleFormDialogVisible();
}

function onUpdateSector(sector: Sector) {
  setSelectedSector(sector);
  parentSectorCode.value = findSectorParentCode(sectors.value[0], sector?.code);
  toggleFormDialogVisible();
}

async function onRemoveSector(sector: Sector) {
  await setSelectedSector(sector);
  toggleRemoveDialogVisible();
}

function onCloseSectorRemove(refresh: boolean = false) {
  toggleRemoveDialogVisible(false);
  onClose(refresh);
}

function onCloseSectorForm(refresh: boolean = false) {
  toggleFormDialogVisible(false);
  onClose(refresh);
}

async function setSelectedSector(sector?: Sector) {
  selectedSector.value = sector;
}

function onClose(refresh: boolean = false) {
  setSelectedSector();

  if (refresh) {
    onSearchSectors();
  }
}

function canUpdateSector(sectorCode: number): boolean {
  return grantedSectors.value
    .map((grantedSector) => grantedSector.code)
    .includes(sectorCode);
}

function getClass(row: unknown): string {
  let classes = "sector-row";
  if (!row?.row?.statuses.granted) {
    classes = `${classes} not-granted-sector`;
  }
  return classes;
}

onMounted(onSearchSectors);
</script>

<template>
  <page-component>
    <search-bar
      v-model:search-query="searchQuery"
      :search-placeholder="t('sectors.search')"
      @clear="onSearchSectors"
      @search="onSearchSectors"
    />

    <container-component
      :px="0"
      :py="0"
      :is-loading="isLoading && !!sectorsTree"
      :error="error"
    >
      <el-table
        v-loading="isLoading"
        class="tree-table my-8"
        default-expand-all
        :data="sectorsTree"
        :fit="true"
        :empty-text="t('sectors.empty')"
        row-key="code"
        :row-class-name="getClass"
        :cell-style="getCellStyle"
      >
        <el-table-column :label="t('sectors.header.name')">
          <template #default="scope">
            <el-button
              class="p-6"
              :disabled="!canUpdateSector(scope.row?.code)"
              text
              @click="onSelectSector(scope.row)"
            >
              <span
                class="text-gray-700 font-medium text-[1.14286em] truncate w-full"
              >
                {{ scope.row.name }}
              </span>
            </el-button>
          </template>
        </el-table-column>
        <el-table-column width="50">
          <template #default="scope">
            <lxc-loader
              v-if="scope.row?.code == selectedSector?.code"
              class="pl-3"
            />
            <sector-actions
              v-else
              :is-affected="
                canUpdateSector(scope.row?.code) &&
                can(ACLRoles.CAN_WRITE_SECTOR)
              "
              :is-top-level="scope.row.isRootSector"
              :can-create="
                getSectorDepth(sectorsTree, scope.row?.code) < MAX_SECTOR_DEPTH
              "
              @create="onCreateSector(scope.row)"
              @update="onUpdateSector(scope.row)"
              @remove="onRemoveSector(scope.row)"
            />
          </template>
        </el-table-column>
      </el-table>
    </container-component>

    <sector-form
      v-model:show="formDialogVisible"
      :parent-sector-code="parentSectorCode!"
      :sector="selectedSector"
      @close="onCloseSectorForm(false)"
      @change="onCloseSectorForm(true)"
    />

    <sector-remove
      :is-visible="removeDialogVisible"
      :sector="selectedSector"
      @close="onCloseSectorRemove(false)"
      @change="onCloseSectorRemove(true)"
    />
  </page-component>
</template>

<style lang="scss" scoped>
:deep(.sector-row) .el-table__cell {
  padding: 0;
}
:deep(.sector-row) .cell {
  display: flex;
  align-items: center;

  .el-table__expand-icon {
    width: $row-expand-icon-size;
    line-height: $row-expand-icon-size;
    height: $row-expand-icon-size;
  }

  .el-button {
    font-family: "Montserrat", sans-serif;
    text-transform: none;
    line-height: $row-expand-icon-size;
    flex: 1 1 100%;
    justify-content: start;
  }
}
</style>
